import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const MODULES = [
  {
    title: 'Chat Contextual',
    description: 'A Vérixa mantém o fio da conversa. Cada resposta é construída com base no contexto anterior — não em respostas genéricas soltas.',
    color: '#7a00ff',
  },
  {
    title: 'Arquitetura Híbrida',
    description: 'Backend em nuvem (Render) com sistema de fallback. Se um provedor falha, outro assume. A Vérixa não para.',
    color: '#00f0ff',
  },
  {
    title: 'Assistente Real',
    description: 'Foco em ser útil hoje: texto, código, análise, perguntas. Sem exagerar capacidades. Sem prometer o que não existe.',
    color: '#ff00c8',
  },
  {
    title: 'Em Construção',
    description: 'Multimodal, Aurora e Vérixa World estão em desenvolvimento. Estrutura pronta, integração em andamento.',
    color: '#00ff9c',
  },
];

function SentienceCard({ module, index }: { module: typeof MODULES[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    };

    card.addEventListener('mousemove', handleMouseMove);
    return () => card.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div
      ref={cardRef}
      className="sentience-card group relative p-6 rounded-panel cursor-grab active:cursor-grabbing transition-transform duration-300 hover:scale-[1.02]"
      style={{
        background: 'rgba(255,255,255,0.03)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        border: '1px solid rgba(255,255,255,0.08)',
        animationDelay: `${index * 0.15}s`,
      }}
    >
      {/* Glow effect following mouse */}
      <div
        className="absolute inset-0 rounded-panel opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(300px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${module.color}15, transparent 60%)`,
        }}
      />

      {/* Accent line */}
      <div
        className="absolute top-0 left-6 right-6 h-[2px] rounded-full"
        style={{
          background: `linear-gradient(90deg, transparent, ${module.color}, transparent)`,
          opacity: 0.6,
        }}
      />

      <h3
        className="font-syncopate text-sm uppercase tracking-[0.08em] mb-3"
        style={{ color: module.color, textShadow: `0 0 20px ${module.color}40` }}
      >
        {module.title}
      </h3>
      <p className="text-slate-400 text-sm leading-relaxed">
        {module.description}
      </p>
    </div>
  );
}

export default function SobreCriadora() {
  const sectionRef = useRef<HTMLElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Animate left column
    gsap.fromTo(
      leftRef.current,
      { opacity: 0, x: -40 },
      {
        opacity: 1,
        x: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 70%',
          toggleActions: 'play none none none',
        },
      }
    );

    // Animate cards with stagger
    const cards = cardsRef.current?.querySelectorAll('.sentience-card');
    if (cards) {
      gsap.fromTo(
        cards,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 60%',
            toggleActions: 'play none none none',
          },
        }
      );
    }
  }, []);

  return (
    <section
      id="sobre"
      ref={sectionRef}
      className="relative w-full py-32 px-6"
      style={{ background: '#050505', zIndex: 5 }}
    >
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16">
        {/* Left Column - Sticky */}
        <div ref={leftRef} className="lg:w-[40%] lg:sticky lg:top-32 lg:self-start">
          <span
            className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-4 block"
            style={{ color: '#00f0ff' }}
          >
            A Criadora
          </span>

          <h2
            className="font-syncopate text-white uppercase leading-[1.1]"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              textShadow: '0 0 40px rgba(122,0,255,0.2)',
            }}
          >
            Cris O. Santos
          </h2>

          <p className="mt-4 text-lg text-white/80 font-medium">
            Técnica em Segurança do Trabalho. Criadora da Vérixa.
          </p>

          <div className="mt-8 space-y-6">
            <p className="text-slate-400 leading-relaxed">
              Mulher trans. Técnica em segurança do trabalho. Enfrentou dificuldades reais,
              não teve apoio, se reconstruiu e criou a Vérixa a partir disso.
            </p>
            <p className="text-slate-400 leading-relaxed">
              Não foi em um laboratório corporativo. Foi na resistência diária,
              na necessidade de criar algo que ninguém mais criaria para pessoas
              como ela.
            </p>
            <p className="text-slate-400 leading-relaxed">
              A Vérixa é um produto em construção — honesto sobre o que já faz
              e claro sobre o que ainda está sendo desenvolvido.
            </p>
          </div>

          {/* Avatar */}
          <div className="mt-10 flex items-center gap-4">
            <div className="relative w-16 h-16 rounded-full overflow-hidden" style={{
              boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)',
            }}>
              <img
                src="/assets/verixa-avatar.png"
                alt="Vérixa Avatar"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">Vérixa IA</p>
              <p className="text-slate-500 text-xs">Criada por Cris Oliveira</p>
            </div>
          </div>

          {/* Diversity badge */}
          <div
            className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-pill border"
            style={{
              borderColor: 'rgba(255,0,200,0.3)',
              background: 'rgba(255,0,200,0.05)',
            }}
          >
            <span className="text-lg">🏳️‍⚧️</span>
            <span className="text-xs text-slate-300 uppercase tracking-[0.08em]">
              Diversidade em tecnologia
            </span>
          </div>
        </div>

        {/* Right Column - Cards */}
        <div ref={cardsRef} className="lg:w-[60%] grid grid-cols-1 sm:grid-cols-2 gap-6">
          {MODULES.map((mod, i) => (
            <SentienceCard key={mod.title} module={mod} index={i} />
          ))}

          {/* Extra philosophy card */}
          <div
            className="sm:col-span-2 p-6 rounded-panel"
            style={{
              background: 'linear-gradient(135deg, rgba(122,0,255,0.08), rgba(0,240,255,0.05))',
              border: '1px solid rgba(122,0,255,0.2)',
            }}
          >
            <p className="text-white/90 text-sm leading-relaxed italic">
              "A tecnologia mais poderosa não é aquela que substitui humanos.
              É aquela que amplifica quem nunca teve espaço para ser ouvido."
            </p>
            <p className="text-slate-500 text-xs mt-3 uppercase tracking-[0.1em]">
              — Cris Oliveira
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
